import React, { Fragment, useMemo } from 'react';
import spacetime from 'spacetime';

import { default as config } from '../config';

import { guid } from '../utils';

import { saveCategory } from '../actions/categoryLanding';
import { getProductsBySku } from '../actions/home';

import Alert from 'react-s-alert';
import HomeNav from '../components/HomeNav.jsx';
import Helmet from 'react-helmet';
import Input from '../components/form/FormikInput';
import Select from '../components/form/FormikSelect';
import TextArea from '../components/form/FormikTextArea.jsx';
import ProductImage from '../components/product/ProductImage.jsx';
import IconPicker from '../components/IconPicker';
import ImageUploader from '../components/ImageUploader';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import SkuListEditor from '../components/SkuListEditor';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@reach/tabs';
import { Formik } from 'formik';
import { useDeleteCategoryMutation, useGetCategoryQuery, useGetCollectionsQuery } from '../services/category';
import { useSelector } from 'react-redux';
import { userPermissions } from './UserDashboard';

const PAGE_TYPES = {
    DYNAMIC: 0,
    CURATED: 1,
    LANDING: 2
};

const blankArray = Array.from({ length: 4 });

const getSlug = (url) => {
    if (url.includes('INACTIVE')) return url;
    return url.substring(1);
};

const getDrafts = (url) => {
    let local = window.localStorage.getItem(url);
    let data = local ? JSON.parse(local) : [];
    return data.map((draft) => ({
        ...draft,
        snapshots: typeof draft.snapshots === 'string' ? JSON.parse(draft.snapshots) : draft.snapshots
    }));
};

const useDraft = (url, draftNumber) => {
    const [drafts, setDrafts] = React.useState([]);
    const [isDraft, setIsDraft] = React.useState(false);
    const [draft, setDraft] = React.useState(null);

    const fullUrl = useMemo(() => `/${url}`, [url]);

    React.useEffect(() => {
        const _drafts = getDrafts(fullUrl);
        if (_drafts) {
            setDrafts(_drafts);
            if (_drafts[draftNumber]) {
                setDraft(_drafts[draftNumber]);
                setIsDraft(true);
            } else {
                setDraft(null);
                setIsDraft(false);
            }
        }
    }, [draftNumber, fullUrl]);

    return {
        isDraft,
        drafts,
        draft
    };
};

export const UnAuthView = () => {
    return (
        <div className="container py-6">
            <h1>Unauthorized</h1>
            <p>You don't have permissions to view this page.</p>
        </div>
    );
};

const CategoryView = () => {
    const session = useSelector((state) => state.session);
    const isContentAdmin = session?.permissions?.includes(userPermissions.CONTENT_ADMIN);
    const location = useLocation();
    const [queryParams] = useSearchParams();
    const queryUrl = queryParams.get('url');
    const queryDraft = queryParams.get('draft');
    const slug = getSlug(queryUrl);
    const navigate = useNavigate();

    const [currentType, setCurrentType] = React.useState(0);
    const [products, setProducts] = React.useState([]);
    const [showIconPicker, setShowIconPicker] = React.useState(false);

    const { isDraft, draft, drafts } = useDraft(slug, queryDraft);
    const { data: _category } = useGetCategoryQuery(slug, { skip: !isContentAdmin || isDraft });
    const { data: collections } = useGetCollectionsQuery(null, { skip: !isContentAdmin });
    const category = isDraft ? draft : _category;

    const [deleteCategory, { isLoading: isDeleting }] = useDeleteCategoryMutation();

    const getCategoryType = React.useCallback(() => {
        if (category?.query) return PAGE_TYPES.DYNAMIC;
        if (category?.sku_list?.length) return PAGE_TYPES.CURATED;
        if (category?.snapshots?.length) return PAGE_TYPES.LANDING;
    }, [category]);

    const getCleanSlug = (url = queryUrl) => {
        return url.split('/').filter(Boolean).join('-');
    };

    React.useEffect(() => {
        (async () => {
            let snaps = category?.snapshots || [];

            let skus = [];
            snaps.forEach((snap) => {
                if (snap.products.join(';').includes('/')) {
                    return;
                }
                skus = [...skus, ...snap.products];
            });

            if (skus.length) {
                let { data: prods } = await getProductsBySku(skus);
                setProducts(prods);
            }
        })();
    }, [category?.snapshots, queryDraft, queryUrl, slug]);

    React.useEffect(() => {
        setCurrentType(getCategoryType());
    }, [getCategoryType]);

    const buildSnapshots = (data) => {
        if (!data) return [];
        let snaps = [];
        data.forEach((snap) => {
            if (!snap.products.filter(Boolean).length) return false;
            snaps.push(snap);
        });
        return snaps;
    };

    const handleDeleteDraft = () => {
        let draftIndex = parseInt(queryDraft, 10);
        let newDrafts = drafts.filter((d, i) => i !== draftIndex);
        window.localStorage.setItem(queryUrl, JSON.stringify(newDrafts));
        navigate({
            pathname: location.pathname,
            search: `url=${queryUrl}`
        });
    };

    const toggleIconPicker = () => {
        setShowIconPicker(!showIconPicker);
    };

    let title = `Edit ${category?.category_name} ${
        isDraft ? `[DRAFT] UPDATED: ${spacetime(category?.updated).format('nice-day')}` : ''
    }`;

    const handleDelete = async () => {
        if (window.confirm('Are you sure? This cannot be undone.')) {
            try {
                await deleteCategory(slug).unwrap();
                window.localStorage.removeItem(queryUrl);
                navigate('/categories');
                Alert.success('Category Deleted');
            } catch {
                Alert.error('Something went wrong deleting the category.');
            }
        }
    };

    if (!isContentAdmin) {
        return <UnAuthView />;
    }

    return (
        <Fragment>
            <HomeNav />
            <div className="container-fluid py-10">
                <Helmet>
                    <title>{title}</title>
                </Helmet>
                <Formik
                    enableReinitialize={true}
                    initialValues={{
                        draft_name: category?.draft_name || '',
                        notes: category?.notes || '',
                        display_name: category?.display_name || '',
                        description: category?.description || '',
                        banner_image: category?.banner_image || '',
                        banner_text: category?.banner_text || '',
                        short_description: category?.short_description || '',
                        collection_icon: category?.collection_icon || '',
                        curated_for: category?.curated_for || '',
                        sku_list: category?.sku_list || null,
                        ranked: category?.ranked || false,
                        query: category?.query ? JSON.stringify(category?.query, null, '    ') : '' || null,
                        snapshots:
                            category?.snapshots?.map((snap) => {
                                return {
                                    ...snap,
                                    id: snap.id || guid(),
                                    link_text: snap.link_text || `View all ${snap.title}`.trim()
                                };
                            }) || null
                    }}
                >
                    {({ values, handleChange, setFieldValue }) => {
                        const makeObject = (stringify = false) => {
                            let data = {
                                display_name: values.display_name,
                                description: values.description,
                                banner_image: values.banner_image,
                                banner_text: values.banner_text,
                                short_description: values.short_description,
                                collection_icon: values.collection_icon || '',
                                curated_for: values.curated_for,
                                sku_list: null,
                                ranked: false,
                                query: null,
                                snapshots: null
                            };

                            if (currentType === PAGE_TYPES.LANDING) {
                                let snaps = buildSnapshots(values.snapshots);
                                if (snaps) {
                                    data.snapshots = stringify ? JSON.stringify(snaps) : snaps;
                                }
                            }

                            if (currentType === PAGE_TYPES.DYNAMIC) {
                                let query = values.query || '';
                                data.query = query;
                            }

                            if (currentType === PAGE_TYPES.CURATED) {
                                data.sku_list = values.sku_list || null;
                                data.ranked = values.ranked || false;
                            }

                            return data;
                        };
                        const handlePublish = async () => {
                            try {
                                let isDraft = queryDraft && drafts[queryDraft];
                                let data = makeObject(true);
                                if (currentType === PAGE_TYPES.CURATED && !data?.sku_list?.length) {
                                    Alert.error('You cannot publish a curated list with no SKUs!');
                                    return false;
                                }

                                await saveCategory(slug, data);
                                Alert.success('Published!');
                                if (isDraft) handleDeleteDraft();
                            } catch {
                                Alert.error('Error Publishing. Please contact Web Dev.');
                            }
                        };
                        return (
                            <div className="row">
                                <div className="col-xs-12 col-md-8 first-md">
                                    <h2 className="spaced-single">{title}</h2>
                                    <Input
                                        id="display_name"
                                        label="Display Name"
                                        value={values.display_name}
                                        onChange={handleChange}
                                    />

                                    <Input
                                        id="short_description"
                                        label="Short Description"
                                        value={values.short_description}
                                        onChange={handleChange}
                                    />

                                    <TextArea
                                        id="description"
                                        label="Description"
                                        value={values.description}
                                        onChange={handleChange}
                                        rows={5}
                                    />

                                    <ImageUploader
                                        className="mb-6"
                                        label="Upload Banner"
                                        bucket="shop_category_banners"
                                        id={getCleanSlug()}
                                        image={values.banner_image}
                                        onUpload={(url) => setFieldValue('banner_image', url)}
                                        showWarning={false}
                                        unique
                                        allowRemoveImage
                                    />

                                    <Input
                                        id="banner_text"
                                        value={values.banner_text}
                                        onChange={handleChange}
                                        label="Banner Alt Text"
                                    />

                                    <Select
                                        id="curated_for"
                                        className="spaced-double"
                                        label="Curated For"
                                        value={values.curated_for || ''}
                                        onChange={handleChange}
                                        multiple
                                    >
                                        {config.product_formats.map((format) => (
                                            <option key={format} value={format}>
                                                {format}
                                            </option>
                                        ))}
                                    </Select>

                                    <Tabs index={currentType || 0} onChange={(index) => setCurrentType(index)}>
                                        <TabList>
                                            <Tab>Dynamic List</Tab>
                                            <Tab>Curated List</Tab>
                                            <Tab>Landing Page</Tab>
                                        </TabList>
                                        <TabPanels className="py-6">
                                            <TabPanel>
                                                <TextArea
                                                    id="query"
                                                    label="Dynamic Query"
                                                    value={values.query || ''}
                                                    onChange={(e) => {
                                                        setFieldValue('query', e.target.value);
                                                    }}
                                                    rows={10}
                                                />
                                            </TabPanel>
                                            <TabPanel>
                                                <h3>Manage SKUs</h3>
                                                <div className="mb-6">
                                                    <SkuListEditor
                                                        skus={values.sku_list || []}
                                                        ranked={values.ranked}
                                                        onChange={({ sku_list, ranked }) => {
                                                            if (ranked) {
                                                                setFieldValue('ranked', ranked);
                                                            }
                                                            setFieldValue('sku_list', sku_list);
                                                        }}
                                                    />
                                                </div>
                                            </TabPanel>
                                            <TabPanel>
                                                <div className="row middle-sm spaced-double">
                                                    <div className="col-xs-12 col-sm-8">
                                                        <h5 className="collapse-sm">Landing Page Snapshots</h5>
                                                    </div>
                                                    <div className="col-xs-12 col-sm-4 end-sm">
                                                        <button
                                                            className="button full-xs auto-sm"
                                                            onClick={() => {
                                                                let newSnaps = [
                                                                    ...(values.snapshots || []),
                                                                    {
                                                                        id: guid(),
                                                                        title: '',
                                                                        url: '',
                                                                        image_type: 'hero',
                                                                        type: 'product',
                                                                        products: ['', '', '', '']
                                                                    }
                                                                ];
                                                                setFieldValue('snapshots', newSnaps);
                                                            }}
                                                        >
                                                            Add New Snapshot
                                                        </button>
                                                    </div>
                                                </div>

                                                {(values.snapshots || []).map((snap, snapIndex) => (
                                                    <div key={snap.id} className="spaced-double">
                                                        <Input
                                                            id={`snapshots[${snapIndex}].title`}
                                                            label="Title"
                                                            value={snap.title}
                                                            placeholder="Snapshot Title"
                                                            onChange={handleChange}
                                                        />

                                                        <Input
                                                            id={`snapshots[${snapIndex}].url`}
                                                            label="Link URL"
                                                            value={snap.url}
                                                            placeholder="Link URL"
                                                            onChange={handleChange}
                                                        />

                                                        <Input
                                                            id={`snapshots[${snapIndex}].link_text`}
                                                            label="Link Text"
                                                            value={snap.link_text}
                                                            placeholder="Link Text"
                                                            onChange={handleChange}
                                                        />

                                                        <div className="row">
                                                            <div className="col-xs-12 col-sm-6">
                                                                <Select
                                                                    id={`snapshots[${snapIndex}].type`}
                                                                    label="What are you featuring?"
                                                                    value={snap.type || ''}
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                    }}
                                                                >
                                                                    <option value="product">Products</option>
                                                                    <option value="collection">Collections</option>
                                                                </Select>
                                                            </div>
                                                            <div className="col-xs-12 col-sm-6">
                                                                {(snap.type === 'product' || 'product') && (
                                                                    <Select
                                                                        id={`snapshots[${snapIndex}].image_type`}
                                                                        label="Image Type"
                                                                        value={snap.image_type || ''}
                                                                        onChange={handleChange}
                                                                    >
                                                                        <option value="hero">Hero</option>
                                                                        <option value="cover">Cover</option>
                                                                    </Select>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="row f-align-end">
                                                            {blankArray.map((value, index) => {
                                                                let sku = snap.products[index];
                                                                let collection = collections?.find((c) => {
                                                                    return c.category_name === sku;
                                                                });
                                                                let prod = products.find((p) => p.sku === `${sku}`);
                                                                let title = 'NOT FOUND';
                                                                if (collection) {
                                                                    title = collection.display_name;
                                                                }
                                                                if (prod) {
                                                                    title = prod.title;
                                                                }
                                                                return (
                                                                    <div key={`snap_${index}`} className="col-md-3">
                                                                        {prod && (
                                                                            <ProductImage
                                                                                product={prod}
                                                                                width={200}
                                                                                imageType={snap.image_type}
                                                                            />
                                                                        )}
                                                                        {collection && (
                                                                            <img
                                                                                src={`${config.shop_url}${collection.collection_icon}`}
                                                                                alt="collection icon"
                                                                            />
                                                                        )}
                                                                        <p
                                                                            style={{
                                                                                whiteSpace: 'nowrap',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis'
                                                                            }}
                                                                            title={title}
                                                                        >
                                                                            <strong>{title}</strong>
                                                                        </p>
                                                                        <Input
                                                                            id={`snapshots[${snapIndex}].products[${index}]`}
                                                                            value={sku}
                                                                            placeholder="SKU"
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                setFieldValue(
                                                                                    `snapshots[${snapIndex}].products[${index}]`,
                                                                                    value
                                                                                );
                                                                                if (!value || value.length < 4) return;
                                                                                if (
                                                                                    products.find(
                                                                                        (p) =>
                                                                                            p.sku.toString() ===
                                                                                            value.toString()
                                                                                    )
                                                                                )
                                                                                    return;

                                                                                if (
                                                                                    values.snapshots[snapIndex].type ===
                                                                                    'collection'
                                                                                )
                                                                                    return;

                                                                                getProductsBySku([value]).then(
                                                                                    ({ data }) => {
                                                                                        setProducts((products) => [
                                                                                            ...products,
                                                                                            ...data
                                                                                        ]);
                                                                                    }
                                                                                );
                                                                            }}
                                                                        />
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        <button
                                                            className="button button-danger button-full button-small"
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setFieldValue(
                                                                    'snapshots',
                                                                    values.snapshots.filter(
                                                                        (snap, index) => index !== snapIndex
                                                                    )
                                                                );
                                                            }}
                                                        >
                                                            Remove Snapshot
                                                        </button>
                                                    </div>
                                                ))}
                                            </TabPanel>
                                        </TabPanels>
                                    </Tabs>

                                    <div>
                                        <h5>Collection Icon</h5>
                                        {values.collection_icon ? (
                                            <div>
                                                <img
                                                    src={`${config.shop_url}${values.collection_icon}`}
                                                    style={{
                                                        width: '100px',
                                                        display: 'inline-block',
                                                        verticalAlign: 'middle'
                                                    }}
                                                    alt="collection icon"
                                                />
                                                <button
                                                    className="button mr-4"
                                                    type="button"
                                                    onClick={toggleIconPicker}
                                                >
                                                    Change Icon
                                                </button>
                                                <button
                                                    className="button button-danger"
                                                    type="button"
                                                    onClick={() => setFieldValue('collection_icon', null)}
                                                >
                                                    Remove Icon
                                                </button>
                                            </div>
                                        ) : (
                                            <button className="button" onClick={toggleIconPicker}>
                                                Select Icon
                                            </button>
                                        )}

                                        <IconPicker
                                            visible={showIconPicker}
                                            onSelect={(icon) => {
                                                setFieldValue('collection_icon', icon);
                                                setShowIconPicker(false);
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="col-xs-12 first-xs col-md-4 spaced-xs collapse-md category-edit-sidebar-wrapper">
                                    <div className="category-edit-sidebar">
                                        <Select
                                            label="Versions"
                                            value={queryDraft || ''}
                                            onChange={(event) => {
                                                let value = event.target.value;
                                                if (value > -1) {
                                                    queryParams.set('draft', value);
                                                } else {
                                                    queryParams.delete('draft');
                                                }

                                                navigate({
                                                    pathname: location.pathname,
                                                    search: queryParams.toString()
                                                });
                                            }}
                                        >
                                            <option value={-1}>Live Production</option>
                                            {drafts.map((draft, i) => (
                                                <option key={`draft_${i}`} value={i}>
                                                    {draft.draft_name}
                                                </option>
                                            ))}
                                        </Select>
                                        {isDraft && (
                                            <div>
                                                <Input
                                                    id="draft_name"
                                                    label="Draft Name"
                                                    value={values.draft_name}
                                                    onChange={handleChange}
                                                />
                                                <TextArea
                                                    id="notes"
                                                    rows={4}
                                                    label="Draft Notes"
                                                    value={values.notes}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        )}
                                        <button
                                            className="button button-success full-xs spaced-single"
                                            onClick={handlePublish}
                                        >
                                            Publish
                                        </button>
                                        <button
                                            className="button full-xs spaced-single"
                                            onClick={() => {
                                                let isDraft = queryDraft && !!drafts[queryDraft];
                                                let _drafts = [...drafts];
                                                let timeStamp = Date.now();

                                                if (isDraft) {
                                                    _drafts = _drafts.map((draft, index) => {
                                                        let draftName = `Draft ${spacetime(
                                                            draft.created || timeStamp
                                                        ).format('nice-day')}`;
                                                        if (index === parseInt(queryDraft, 10)) {
                                                            draft = {
                                                                ...draft,
                                                                ...makeObject(),
                                                                draft_name:
                                                                    values.draft_name || draft.draft_name || draftName,
                                                                notes: values.notes || '',
                                                                category_name: queryUrl,
                                                                updated: timeStamp,
                                                                created: draft.created || timeStamp
                                                            };
                                                        }
                                                        return draft;
                                                    });
                                                } else {
                                                    _drafts = [
                                                        ..._drafts,
                                                        {
                                                            ...makeObject(),
                                                            draft_name: `Draft ${spacetime(timeStamp).format(
                                                                'nice-day'
                                                            )}`,
                                                            category_name: queryUrl,
                                                            notes: '',
                                                            created: timeStamp,
                                                            updated: timeStamp
                                                        }
                                                    ];
                                                }

                                                window.localStorage.setItem(queryUrl, JSON.stringify(_drafts));

                                                if (!isDraft) {
                                                    queryParams.set('draft', _drafts.length - 1);
                                                    navigate({
                                                        pathname: location.pathname,
                                                        search: queryParams.toString()
                                                    });
                                                }

                                                Alert.success('Draft Saved!');
                                            }}
                                        >
                                            Save Draft
                                        </button>
                                        <button
                                            className="button button-danger spaced-single"
                                            type="button"
                                            disabled={isDeleting}
                                            onClick={handleDelete}
                                        >
                                            Delete Category
                                        </button>
                                        {isDraft && (
                                            <button
                                                className="button button-danger full-xs spaced-single"
                                                onClick={handleDeleteDraft}
                                            >
                                                Delete Draft
                                            </button>
                                        )}
                                        {!isDraft && (
                                            <p>
                                                <a
                                                    href={`https://shop.playaway.com${category?.category_name}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View Published Page
                                                </a>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                </Formik>
            </div>
        </Fragment>
    );
};
export default CategoryView;
